import { useToast } from "@/components/ui/use-toast";
import CompraRapidaService, { BuscarCompraDto } from "@/services/CompraRapidaService";
import { MASCARA_CPF } from "@/utils/Constants";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { conformToMask } from "react-text-mask";
import { useAuth } from "@/contexts/AuthProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Share2 } from "lucide-react";
import { labelPlural, labelSingularLower } from "@/utils/copyTituloBilhete";
import { formatarNumeroGiro } from "@/utils/formatarNumeroGiro";


export function DetalhesCompraPage() {
    let { idCompra } = useParams();
    const urlParams = new URLSearchParams(window.location.search);
    const navigate = useNavigate();
    const [compra, setCompra] = useState<BuscarCompraDto>();
    const [loading, setLoading] = useState(true)
    const { toast } = useToast()
    const { isAuthorized, usuario } = useAuth();
    const [compartilhando, setCompartilhando] = useState(false);
    const [exibirModalVersaoLimitada, setExibirModalVersaoLimitada] = useState(false);
    const isAndroid = navigator.userAgent.toLowerCase().includes("android");
    const isInstagram = navigator.userAgent.toLowerCase().includes("instagram")

    const tokenCompraRapida = urlParams.get('tokenCompraRapida');
    const compraRapida = urlParams.get('comprarapida');

    useEffect(() => {
        if (!isAuthorized) {
            if (!tokenCompraRapida || !compraRapida) {
                navigate('/')
                return;
            }

            buscarCompraRapida(tokenCompraRapida);
        } else {
            buscarCompra();
        }
    }, [])

    // Função para converter URL de imagem em File
    async function urlToFile(url: string, filename: string): Promise<File> {
        const response = await fetch(url);
        const blob = await response.blob();
        return new File([blob], filename, { type: blob.type });
    }

    async function handleCompartilharTitulos() {
        // Se não houver imagens nem cautelas, não há o que compartilhar
        if ((!compra?.UrlCertificados || compra.UrlCertificados.length === 0) && (!compra?.Cautelas || compra.Cautelas.length === 0)) {
            toast({
                variant: 'destructive',
                description: `Nenhum ${labelSingularLower()} encontrado para compartilhar`,
                duration: 3000
            });
            return;
        }

        try {
            setCompartilhando(true);

            if (navigator.userAgent.toLowerCase().includes("instagram")) {
                toggleModalVersaoLimitada();
            }

            // Prioriza compartilhar imagens se existirem
            if (compra?.UrlCertificados && compra.UrlCertificados.length > 0) {
                const files: File[] = [];
                for (let i = 0; i < compra.UrlCertificados.length; i++) {
                    const url = compra.UrlCertificados[i];
                    const filename = `titulo_${compra.NumeroPedido}_${i + 1}.jpeg`;
                    const file = await urlToFile(url, filename);
                    files.push(file);
                }

                if (navigator.canShare && navigator.canShare({ files })) {
                    await navigator.share({
                        files,
                        title: `${labelPlural()} - Compra ${compra.NumeroPedido}`,
                        text: `${labelPlural()} da compra número ${compra.NumeroPedido} do sorteio ${compra.DataSorteio}`,
                    });
                } else {
                    files.forEach((file) => {
                        const url = URL.createObjectURL(file);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = file.name;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                    });
                }

                return;
            }

            // Caso GIRO: compartilha texto com NumeroTitulo ou Titulos.Numero
            if (compra?.Cautelas && compra.Cautelas.length > 0) {
                const numeros = compra.Cautelas.flatMap((c: any) =>
                    c.NumeroTitulo ? [c.NumeroTitulo] : (c.Titulos || []).map((t: any) => t.Numero)
                ).map(formatarNumeroGiro);

                const text = `${labelPlural()} da compra ${compra.NumeroPedido} - Sorteio ${compra.DataSorteio}\n\n` +
                    numeros.map((n, i) => `${i + 1}. ${n}`).join("\n");

                if (navigator.share) {
                    await navigator.share({
                        title: `${labelPlural()} - Compra ${compra.NumeroPedido}`,
                        text,
                    });
                } else {
                    await navigator.clipboard.writeText(text);
                    toast({
                        variant: 'success',
                        description: 'Números copiados para a área de transferência para compartilhar.',
                        duration: 3000
                    });
                }

                return;
            }

        } catch (error: any) {
            if (error.toString().includes('AbortError')) {
                return;
            }

            console.error(`Erro ao compartilhar ${labelPlural().toLowerCase()}:`, error);
            toast({
                variant: 'destructive',
                description: `Erro ao compartilhar ${labelPlural().toLowerCase()}. Tente novamente.`,
                duration: 4000
            });
        } finally {
            setCompartilhando(false)
        }
    }

    async function buscarCompra() {
        try {
            if (!idCompra) {
                return
            }

            const { Data: result } = await CompraRapidaService.buscarCompra(parseInt(idCompra), usuario?.TokenAcesso!);
            setCompra(result);
        } catch (error: any) {
            toast({
                variant: 'destructive',
                description: error,
                duration: 4000
            });
        } finally {
            setLoading(false);
        }
    }

    async function buscarCompraRapida(token: string) {
        try {
            if (!idCompra) {
                return
            }

            const { Data: result } = await CompraRapidaService.buscarCompraRapida(token);
            setCompra(result);
        } catch (error: any) {
            toast({
                variant: 'destructive',
                description: error,
                duration: 4000
            });
        } finally {
            setLoading(false);
        }
    }

    async function toggleModalVersaoLimitada() {
        setExibirModalVersaoLimitada(!exibirModalVersaoLimitada!)
    }

    return (
        loading ? <>
            Carregando...
        </> : (
            <div className="px-3 pt-2">
                <h1 className="text-center text-primary-foreground font-semibold text-xl">
                    Detalhes da Compra
                </h1>

                <p className="text-primary-foreground">
                    <label className="font-semibold">Sorteio: </label> {compra?.DataSorteio}
                </p>
                <p className="text-primary-foreground">
                    <label className="font-semibold">Numero: </label> {compra?.NumeroPedido}
                </p>
                <p className="text-primary-foreground">
                    <label className="font-semibold">Nome: </label> {compra?.Usuario.Nome}
                </p>
                <p className="text-primary-foreground">
                    <label className="font-semibold">CPF: </label> {conformToMask(compra?.Usuario.CPF ?? '', MASCARA_CPF).conformedValue}
                </p>
                <p className="text-primary-foreground">
                    <label className="font-semibold">{labelPlural()}: </label> {compra?.QuantidadeCautelas}
                </p>
                {
                    // Se for GIRO (sem certificados), exibe o tipo abaixo do título de bilhetes
                    (!compra?.UrlCertificados || compra.UrlCertificados.length === 0) && compra?.Cautelas && compra.Cautelas.length > 0 && (
                        <p className="text-primary-foreground font-semibold mt-2">Tipo: Giro</p>
                    )
                }

                {((compra?.UrlCertificados && compra.UrlCertificados.length > 0) || (compra?.Cautelas && compra.Cautelas.length > 0)) && (
                    <div className="flex justify-center mb-4">
                        {
                            isAndroid && isInstagram ? (
                                <a
                                    href={`${window.location.origin}/compra/extrato-compras?token=${encodeURIComponent(usuario?.TokenAcesso!)}`}
                                    target="_blank"
                                    download
                                >
                                    <Button type="button" className="gap-2" >
                                        <Share2 size={16} />
                                        {
                                            compartilhando ? <span>Compartilhando...</span> : <span>Compartilhar Bilhetes</span>
                                        }
                                    </Button>
                                </a>
                            ) : (

                                <Button
                                    disabled={compartilhando}
                                    className="gap-2"
                                    onClick={handleCompartilharTitulos}
                                >
                                    <Share2 size={16} />
                                    {
                                        compartilhando ? <span>Compartilhando...</span> : <span>Compartilhar Bilhetes</span>
                                    }
                                </Button>
                            )
                        }
                    </div>
                )}

                <div className="mb-20 mt-4 flex flex-col items-center justify-center">
                    {
                        // Se houver imagens, exibe-as
                        compra?.UrlCertificados && compra.UrlCertificados.length > 0 && compra.UrlCertificados.map((img) => (
                            <Card className='border-2 mb-10' key={img}>
                                <CardContent className="p-3">
                                    <img src={img} />
                                </CardContent>
                                <Separator />
                            </Card>
                        ))
                    }

                    {
                        // Se for GIRO (sem imagens), exibir apenas NumeroTitulo em cards simples, lado a lado
                        (!compra?.UrlCertificados || compra.UrlCertificados.length === 0) && compra?.Cautelas && compra.Cautelas.length > 0 && (
                            <div className="flex flex-wrap gap-2 justify-center">
                                {compra.Cautelas.map((cautela: any) => (
                                    <Card
                                        key={cautela.IdCautela}
                                        className="border-2 rounded-md flex items-center justify-center min-w-[5.75rem] w-auto px-2 h-12 bg-yellow-400 text-black"
                                    >
                                        <CardContent className="p-1 text-center">
                                            <span className="font-semibold text-sm tabular-nums">
                                                {formatarNumeroGiro(cautela.NumeroTitulo ?? cautela.Titulos?.[0]?.Numero)}
                                            </span>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )
                    }
                    {
                        !compraRapida && (
                            <Button className="gap-2 mt-4" onClick={() => {
                                navigate(-1);
                            }} >
                                <span>Voltar</span>
                            </Button>
                        )
                    }
                </div>
            </div>
        )
    )
}